import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { map } from 'rxjs'
import { DatePipe } from '@angular/common'
import { CallsService, CallStatus, ClientCall } from '../calls.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import {
  UndoSoftDeletionDialogComponent,
  UndoSoftDeletionDialogData,
  UndoSoftDeletionDialogResult,
} from '../call/undo-soft-deletion-dialog/undo-soft-deletion-dialog.component'
import { DurationPipe } from '../duration.pipe'
import { queryKeys } from '../query-keys'
import { AppTableChip, AppTableColumn, AppTableRowAction, AppTableRowActionEvent, AppTableSource, TableComponent } from '../table/table.component'
import { AvatarComponent } from '../avatar/avatar.component'
import { MatTooltip } from '@angular/material/tooltip'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../utils'
import {
  ExtendRetentionDialogComponent,
  ExtendRetentionDialogData,
  ExtendRetentionDialogResult,
} from './extend-retention-dialog/extend-retention-dialog.component'
import { CallTimelineDialogComponent, CallTimelineDialogData } from '../call-timeline-dialog/call-timeline-dialog.component'
import { ManageCallUsersDialogComponent, ManageCallUsersDialogData } from './manage-call-users-dialog/manage-call-users-dialog.component'
import {
  RetentionExtensionHistoryDialogComponent,
  RetentionExtensionHistoryDialogData,
} from './retention-extension-history-dialog/retention-extension-history-dialog.component'

const DEFAULT_PAGE_SIZE = 25

@Component({
  selector: 'app-admin-calls-page',
  standalone: true,
  imports: [ToolbarComponent, TableComponent, TranslatePipe, AvatarComponent, MatTooltip],
  providers: [DatePipe, DurationPipe],
  templateUrl: './admin-calls-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-calls-page.component.scss',
})
export class AdminCallsPageComponent implements OnInit {
  private callsService = inject(CallsService)
  private dialog = inject(MatDialog)
  private translateService = inject(TranslateService)
  private datePipe = inject(DatePipe)
  private durationPipe = inject(DurationPipe)

  defaultPageSize = DEFAULT_PAGE_SIZE

  // Newest first within whatever window is in force, so the most recent meeting is the first row.
  readonly defaultSort = { columnId: 'date', direction: 'desc' as const }
  private localTimeZone = resolveLocalTimeZone()

  @ViewChild('nameCell', { static: true }) nameCell!: TemplateRef<{ $implicit: ClientCall }>
  @ViewChild('callUsersCell', { static: true }) callUsersCell!: TemplateRef<{ $implicit: ClientCall }>

  /** Meetings a new assistant has been sent to in this sitting, so the action cannot be repeated. */
  private callIdsSentARecoveryAssistant = new Set<number>()

  private extendRetentionPeriodMutation = this.callsService.extendRetentionPeriodMutation()
  private restoreCallMutation = this.callsService.restoreCallMutation()
  private sendRecoveryBotMutation = this.callsService.sendRecoveryBotMutation()

  /**
   * Every status rather than the joinable ones alone: the table's own status filter is what narrows
   * the list now, and it can only offer what the endpoint returns.
   *
   * Keyed on the mutations' own query key, so extending a retention period or restoring a meeting
   * refreshes the table.
   */
  readonly source: AppTableSource<ClientCall> = {
    url: '/api/calls/client',
    params: { includeAllStatuses: true },
    queryKey: queryKeys.calls.client.base,
  }

  rowId = (call: ClientCall): string => String(call.id)

  columns: AppTableColumn<ClientCall>[] = []

  ngOnInit() {
    this.columns = [
      { id: 'id', header: 'COMMON.ID', value: (call) => call.id, numeric: true, tone: 'muted', sortable: true, filter: 'text' },
      // The name is drawn by hand so a soft-deleted meeting can be struck through. Sorting, filtering
      // and the CSV all read `value`, so none of them are affected by how the cell is drawn.
      {
        id: 'name',
        header: 'COMMON.NAME',
        value: (call) => call.name,
        cellTemplate: this.nameCell,
        tone: 'strong',
        sortable: true,
        filter: 'text',
      },
      { id: 'organizationName', header: 'COMMON.ORGANIZATION', value: (call) => call.organizationName, filter: 'facet', autoFit: true },
      {
        id: 'date',
        header: 'COMMON.DATE_AND_TIME',
        // Acting on a meeting that has happened is the common errand, so that is where the list opens.
        defaultDatePreset: 'past',
        value: (call) => new Date(call.date).getTime(),
        display: (call) => this.formatDate(call.date),
        sortable: true,
        filter: 'date',
        autoFit: true,
      },
      { id: 'duration', header: 'COMMON.DURATION', value: (call) => call.duration ?? 0, display: (call) => this.formatDuration(call.duration), numeric: true },
      {
        id: 'users',
        header: 'ADMIN.CALLS.OWNERS',
        // The names are what the column sorts and downloads by; the avatars are how it reads.
        value: (call) => call.users.map((user) => `${user.firstName} ${user.lastName}`).join(', '),
        cellTemplate: this.callUsersCell,
        filter: 'text',
        autoFit: true,
      },
      {
        id: 'status',
        header: 'COMMON.STATUS',
        value: (call) => this.describeStatus(call).label,
        chip: (call) => this.describeStatus(call),
        sortable: true,
        filter: 'facet',
        // The values arrive as they are stored — CREATED and the like — and the reader knows them by
        // the words the status column itself shows.
        facetLabel: (value) => this.describeCallStatus(String(value)),
      },
      {
        id: 'markedToBeDeletedAt',
        header: 'ADMIN.CALLS.SOFT_DELETED_AT',
        value: (call) => (call.markedToBeDeletedAt === null ? 0 : new Date(call.markedToBeDeletedAt).getTime()),
        display: (call) => this.formatDate(call.markedToBeDeletedAt),
        tone: 'muted',
        sortable: true,
        autoFit: true,
      },
      {
        id: 'deletionDate',
        // The date the meeting is destroyed for good, which is what this has always held. Blank for
        // an organization that keeps its meetings, since then there is no such date.
        header: 'ADMIN.CALLS.HARD_DELETION_DATE',
        value: (call) => (call.deletionDate === null ? 0 : new Date(call.deletionDate).getTime()),
        display: (call) => this.formatDate(call.deletionDate),
        tone: 'muted',
        sortable: true,
        autoFit: true,
      },
    ]
  }

  readonly rowActions: AppTableRowAction<ClientCall>[] = [
    {
      id: 'extendRetention',
      label: 'CALLS.EXTEND_RETENTION.BUTTON',
      icon: 'more_time',
      isVisible: (call) => call.canExtendRetentionPeriod && call.markedToBeDeletedAt === null,
    },
    {
      id: 'retentionHistory',
      label: 'CALLS.EXTEND_RETENTION.HISTORY_BUTTON',
      icon: 'history',
      isVisible: (call) => call.retentionExtensionCount > 0,
    },
    // Reading what has happened to a meeting is not acting on it, so it is offered whatever the
    // organization has decided about the actions below.
    { id: 'timeline', label: 'CALLS.ADMIN.SHOW_TIMELINE', icon: 'timeline' },
    {
      id: 'undoSoftDeletion',
      label: 'CALLS.ADMIN.SOFT_DELETION.UNMARK_TITLE',
      icon: 'restore_from_trash',
      isVisible: (call) => call.canActOnCall && call.markedToBeDeletedAt !== null,
    },
    { id: 'manageUsers', label: 'CALLS.ADMIN.MANAGE_CALL_USERS.BUTTON', icon: 'manage_accounts', isVisible: (call) => call.canActOnCall },
    {
      id: 'sendRecoveryAssistant',
      label: 'ADMIN.CALLS.SEND_NEW_ASSISTANT',
      disabledLabel: 'ADMIN.CALLS.SEND_NEW_ASSISTANT_ALREADY_SENT',
      icon: 'smart_toy',
      isVisible: (call) => call.canActOnCall && call.isEligibleForRecoveryAssistant,
      isDisabled: (call) => this.callIdsSentARecoveryAssistant.has(call.id),
    },
  ]

  handleRowAction({ actionId, row }: AppTableRowActionEvent<ClientCall>) {
    const handlers: Record<string, (call: ClientCall) => void> = {
      extendRetention: (call) => this.extendRetentionPeriod(call),
      retentionHistory: (call) => this.showRetentionExtensionHistory(call),
      timeline: (call) => this.showTimeline(call),
      undoSoftDeletion: (call) => this.undoSoftDeletion(call),
      manageUsers: (call) => this.manageCallUsers(call),
      sendRecoveryAssistant: (call) => this.sendRecoveryBot(call),
    }
    handlers[actionId]?.(row)
  }

  // The page has read in the reader's own zone since the retention dialogs moved to it, so a date in
  // the table cannot go back to London's.
  /** Falls back to the stored value, so a status without wording of its own still reads as itself. */
  private describeCallStatus(status: string): string {
    const key = `ADMIN.CALLS.STATUSES.${status}`
    const translated = this.translateService.instant(key)
    return translated === key ? status : translated
  }

  /**
   * Says what the strikethrough means, for the reader who wonders rather than the one who knows.
   *
   * Empty for a meeting that is not soft-deleted, so the tooltip only appears where there is
   * something to explain.
   */
  softDeletionNote(call: ClientCall): string {
    return call.markedToBeDeletedAt === null ? '' : this.translateService.instant('ADMIN.CALLS.SOFT_DELETED_NAME_NOTE')
  }

  private formatDate(value: Date | null): string {
    return value === null ? '-' : (this.datePipe.transform(value, DATE_TIME_FORMAT, undefined, undefined) ?? '-')
  }

  private formatDuration(value: number | null): string {
    return value === null ? '-' : String(this.durationPipe.transform(value) ?? '-')
  }

  /**
   * The lifecycle of a meeting as one chip: what the assistant is doing right now takes precedence
   * over the meeting's own status, which for an ongoing meeting still reads as merely created.
   */
  private describeStatus(call: ClientCall): AppTableChip {
    if (call.markedToBeDeletedAt !== null) {
      return { label: this.translateService.instant('ADMIN.CALLS.STATUS_SOFT_DELETED'), variant: 'danger' }
    }
    if (call.isOngoing) {
      const sessionStatus = call.latestCallSessionStatus
      return {
        label:
          sessionStatus === null
            ? this.translateService.instant('ADMIN.CALLS.STATUS_ONGOING')
            : this.translateService.instant(`CALLS.STATUSES.${sessionStatus}`),
        variant: 'warning',
      }
    }
    if (call.processingStatus !== null) {
      return { label: this.translateService.instant(`CALLS.PROCESSING_STATUSES.${call.processingStatus}`), variant: 'neutral' }
    }
    if (call.status === 'COMPLETED') {
      return { label: this.translateService.instant('CALLS.STATUSES.COMPLETED'), variant: 'success' }
    }
    if (call.status === 'ERRORED' || call.status === 'TIMEOUT') {
      return { label: this.translateService.instant(`CALLS.STATUSES.${call.status}`), variant: 'danger' }
    }
    return { label: this.translateService.instant(`ADMIN.CALLS.STATUSES.${call.status}`), variant: 'neutral' }
  }

  private extendRetentionPeriod(call: ClientCall) {
    // The dialog is drawn from both of the meeting's deletion dates, so one that is missing either of
    // them has no timeline to show.
    if (call.deletionDate === null || call.softDeletionDate === null) {
      return
    }
    const data: ExtendRetentionDialogData = {
      callName: call.name,
      meetingDate: call.date,
      currentSoftDeletionDate: call.softDeletionDate,
      currentDeletionDate: call.deletionDate,
      maximumDeletionDate: call.maximumDeletionDate,
      maximumSoftDeletionDate: call.maximumSoftDeletionDate,
      maximumSoftDeletionDateInTotal: call.maximumSoftDeletionDateInTotal,
      maxSingleExtensionInDays: call.maxSingleExtensionInDays,
      maxTotalExtensionInDays: call.maxTotalExtensionInDays,
      usedTotalExtensionInDays: call.usedTotalExtensionInDays,
      minimumSoftDeletionDate: call.minimumSoftDeletionDate,
      minimumDeletionDate: call.minimumDeletionDate,
    }
    const dialogRef = this.dialog.open<ExtendRetentionDialogComponent, ExtendRetentionDialogData, ExtendRetentionDialogResult | null>(
      ExtendRetentionDialogComponent,
      { width: '640px', data }
    )
    // Dismissing the dialog — escape, the backdrop, Cancel or the cross — closes it with nothing.
    dialogRef
      .afterClosed()
      .pipe(map((result) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.extendRetentionPeriodMutation.mutateAsync({
            callId: call.id,
            deletionDate: result.deletionDate,
            softDeletionDate: result.softDeletionDate,
          })
        }
      })
  }

  private showTimeline(call: ClientCall) {
    const data: CallTimelineDialogData = { id: call.id }
    this.dialog.open(CallTimelineDialogComponent, { width: '900px', maxHeight: '80vh', data, panelClass: 'timeline-dialog' })
  }

  private showRetentionExtensionHistory(call: ClientCall) {
    const data: RetentionExtensionHistoryDialogData = { callId: call.id, callName: call.name }
    this.dialog.open(RetentionExtensionHistoryDialogComponent, { width: '960px', maxWidth: '95vw', data })
  }

  private undoSoftDeletion(call: ClientCall) {
    const data: UndoSoftDeletionDialogData = {
      callId: call.id,
      currentSoftDeletionDate: call.softDeletionDate,
      currentDeletionDate: call.deletionDate,
    }
    const dialogRef = this.dialog.open<UndoSoftDeletionDialogComponent, UndoSoftDeletionDialogData, UndoSoftDeletionDialogResult | null>(
      UndoSoftDeletionDialogComponent,
      { width: '500px', data }
    )
    dialogRef
      .afterClosed()
      .pipe(map((result) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.restoreCallMutation.mutateAsync({ callId: call.id, dates: result.dates })
        }
      })
  }

  private manageCallUsers(call: ClientCall) {
    const data: ManageCallUsersDialogData = {
      callId: call.id,
      callName: call.name,
      organizationId: call.organizationId,
      access: call.access,
      allowRestrictedUsers: call.allowRestrictedUsers,
    }
    this.dialog.open(ManageCallUsersDialogComponent, { width: '560px', data, autoFocus: 'first-heading' })
  }

  private sendRecoveryBot(call: ClientCall) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('ADMIN.CALLS.SEND_NEW_ASSISTANT'),
        description: this.translateService.instant(
          call.status === CallStatus.TIMEOUT || call.status === CallStatus.ERRORED
            ? 'ADMIN.CALLS.SEND_NEW_ASSISTANT_TO_UNRECORDED_DESCRIPTION'
            : 'CALLS.ADMIN.SEND_RECOVERY_BOT.DESCRIPTION'
        ),
      },
    })
    dialogRef
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true))
      .subscribe(async (confirmed) => {
        if (confirmed) {
          // Marked before the request goes out, so a second click cannot land while the first is in
          // flight. A rejected request releases the lock again: the endpoint refuses a duplicate on
          // its own, because a meeting that was sent one is left waiting for a retry.
          this.callIdsSentARecoveryAssistant.add(call.id)
          try {
            await this.sendRecoveryBotMutation.mutateAsync(call.id)
          } catch {
            this.callIdsSentARecoveryAssistant.delete(call.id)
          }
        }
      })
  }
}
